// frontend/smartdocs-admin/src/components/settings/DocumentProcessingSettings.tsx
import React from 'react';
import { useSettings } from '@/context/SettingsContext';
import ToggleSwitch from '@shared/ToggleSwitch';
import SliderInput from '@shared/SliderInput';
import SettingsPage from '@/pages/SettingsPage';
import Card from "@shared/Card";
import Input from "@shared/Input";

const DocumentProcessingSettings: React.FC = () => {
  const { settings, updateSetting, loading } = useSettings();

  const docProcessingSettings = settings.DocumentProcessing || {};

  const handleUpdate = (key: string, value: any) => {
    updateSetting('DocumentProcessing', key, value);
  };

  return (
    <SettingsPage>
      <Card>
        <div className="space-y-6">
      {/*<h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Document Processing Settings</h2>*/}

      {/* Max File Size */}
      <div>
        <label htmlFor="maxFileSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Maximum File Size (MB)</label>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">The maximum allowed size for document uploads.</p>
        <SliderInput
          id="maxFileSize"
          label=""
          min={10}
          max={200}
          step={10}
          value={docProcessingSettings.maxFileSize || 50}
          onChange={(value) => handleUpdate('maxFileSize', value)}
          disabled={loading}
        />
      </div>

      {/* Supported File Types */}
      <div>
        <label htmlFor="supportedFileTypes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Supported File Types (comma-separated)</label>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">List of file extensions allowed for document uploads (e.g., pdf, docx, txt).</p>
        <Input
          type="text"
          id="supportedFileTypes"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          value={docProcessingSettings.supportedFileTypes || 'pdf, docx, txt, pptx, xlsx'}
          onChange={(e) => handleUpdate('supportedFileTypes', e.target.value)}
          disabled={loading}
        />
      </div>

      {/* Enable OCR */}
      <div className="flex items-center justify-between">
        <div>
          <label htmlFor="enableOCR" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Enable OCR for Images/Scans</label>
          <p className="text-sm text-gray-500 dark:text-gray-400">Automatically extract text from images and scanned documents.</p>
        </div>
        <ToggleSwitch
          id="enableOCR"
          label=""
          checked={docProcessingSettings.enableOCR || false}
          onChange={(e) => handleUpdate('enableOCR', e.target.checked)}
          disabled={loading}
        />
      </div>

      {/* Auto-tagging */}
      <div className="flex items-center justify-between">
        <div>
          <label htmlFor="autoTagging" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Enable Auto-tagging</label>
          <p className="text-sm text-gray-500 dark:text-gray-400">Automatically generate tags for uploaded documents based on content.</p>
        </div>
        <ToggleSwitch
          id="autoTagging"
          label=""
          checked={docProcessingSettings.autoTagging || false}
          onChange={(e) => handleUpdate('autoTagging', e.target.checked)}
          disabled={loading}
        />
      </div>

      {/* Document Retention Period */}
      <div>
        <label htmlFor="retentionPeriod" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Document Retention Period (days)</label>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Number of days to retain documents before automatic archival or deletion. Set to 0 for indefinite.</p>
        <SliderInput
          id="retentionPeriod"
          label=""
          min={0}
          max={365}
          step={30}
          value={docProcessingSettings.retentionPeriod || 0}
          onChange={(value) => handleUpdate('retentionPeriod', value)}
          disabled={loading}
        />
      </div>
    </div>
      </Card>
    </SettingsPage>
  );
};

export default DocumentProcessingSettings;
